import type { ReactNode } from "react";
import type { Certificate } from "../../../shared/models/certificate";
import type { CertificatePolicy } from "../../../shared/models/certificate_policy";
import type { DeviceGroup } from "../../../shared/models/device_group";
import type { BulkRevocationResponse } from "../../../shared/models/bulk_revocation";

// ─── Modal Type Registry ─────────────────────────────────────────────────────

export interface GroupDetailProps {
  group: DeviceGroup;
  policy?: CertificatePolicy;
  onPolicyClick?: () => void;
}

export interface PolicyDetailProps {
  policy: CertificatePolicy;
}

export interface CertificateDetailProps {
  cert: Certificate;
  groupName?: string;
  policyName?: string;
  onGroupClick?: () => void;
  onPolicyClick?: () => void;
  onRevoke?: () => void;
}

export interface RevokeCertificateProps {
  certificate: Certificate;
  onConfirm: () => Promise<void>;
}

export interface BulkRevokeProps {
  certificates: { deviceId: string; certvalidfrom: string }[];
  onComplete: (result: BulkRevocationResponse) => void;
}

export interface RevokeAllProps {
  groupName: string;
  certificates: { deviceId: string; certvalidfrom: string }[];
  onComplete: (result: BulkRevocationResponse) => void;
}

export interface CreateGroupProps {
  policies: { id: string; name: string }[];
}

export interface EditPolicyProps {
  group: DeviceGroup;
  policies: { id: string; name: string }[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CreateNotificationProps {}

export interface CreatePolicyProps {
  duplicateFrom?: CertificatePolicy;
}

export interface DeleteConfirmProps {
  title: string;
  message: ReactNode;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
  confirmingLabel?: string;
}

export type ModalEntry =
  | { type: "groupDetail"; props: GroupDetailProps }
  | { type: "policyDetail"; props: PolicyDetailProps }
  | { type: "certificateDetail"; props: CertificateDetailProps }
  | { type: "revokeCertificate"; props: RevokeCertificateProps }
  | { type: "bulkRevoke"; props: BulkRevokeProps }
  | { type: "revokeAll"; props: RevokeAllProps }
  | { type: "createGroup"; props: CreateGroupProps }
  | { type: "editPolicy"; props: EditPolicyProps }
  | { type: "createNotification"; props: CreateNotificationProps }
  | { type: "createPolicy"; props: CreatePolicyProps }
  | { type: "deleteConfirm"; props: DeleteConfirmProps };

// ─── Modal Stack Store ───────────────────────────────────────────────────────

export interface StackEntry {
  id: number;
  modal: ModalEntry;
}

let stack: StackEntry[] = [];
let nextId = 0;
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

export function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getSnapshot(): StackEntry[] {
  return stack;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const modalManager = {
  /** Open a modal. Returns its ID for targeted closing. */
  open<T extends ModalEntry["type"]>(
    type: T,
    props: Extract<ModalEntry, { type: T }>["props"]
  ): number {
    const id = nextId++;
    stack = [...stack, { id, modal: { type, props } as ModalEntry }];
    notify();
    return id;
  },

  /** Close the topmost modal, or a specific one by ID. */
  close(id?: number): void {
    if (id !== undefined) {
      stack = stack.filter((e) => e.id !== id);
    } else {
      stack = stack.slice(0, -1);
    }
    notify();
  },

  /** Close all modals. */
  closeAll(): void {
    stack = [];
    notify();
  },
};
